import { MapPin, Ruler, Package, CalendarDays, Truck, PoundSterling } from "lucide-react";
import { SERVICE_LABEL, JOB_TYPE_LABEL } from "@/lib/fitters";
import { floorLabel } from "@/lib/api";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";

const JOB_TYPE_STYLE = {
  collection: "bg-sky-100 text-sky-800",
  fitting: "bg-violet-100 text-violet-800",
  collection_fitting: "bg-emerald-100 text-emerald-800",
  delivery: "bg-amber-100 text-amber-800",
  measuring: "bg-teal-100 text-teal-800",
};

export function FitterJobCard({ job, onView, onAccept, acceptLabel = "Accept job", testId }) {
  const { unit } = useUnit();
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid={testId || `job-card-${job.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span
            className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${JOB_TYPE_STYLE[job.job_type] || "bg-slate-100 text-slate-700"}`}
          >
            {JOB_TYPE_LABEL[job.job_type] || job.job_type}
          </span>
          <h3 className="font-display font-semibold text-lg text-slate-900 mt-2">{job.title}</h3>
          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
            {job.general_location} · approx {job.distance_miles != null ? job.distance_miles : Math.round((job.distance_km || 0) / 1.60934)} miles away
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-2xl text-[#0F3D2E] flex items-center gap-0.5">
            <PoundSterling className="w-5 h-5" aria-hidden="true" />
            {job.fitter_payment_gbp}
          </div>
          <div className="text-[11px] text-slate-500">fitter payment</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
        <Fact icon={Package} label="Flooring" value={floorLabel(job.flooring_type)} />
        {job.room_width_m && job.room_length_m ? (
          <Fact icon={Ruler} label="Room" value={dimPairLabel(job.room_width_m, job.room_length_m, unit)} />
        ) : (
          <Fact icon={Truck} label="Collection" value={job.collection_required ? "Required" : "Not needed"} />
        )}
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
          Services required
        </p>
        <div className="flex flex-wrap gap-1.5">
          {job.services_required.map((s) => (
            <span
              key={s}
              className="text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1"
            >
              {SERVICE_LABEL[s] || s}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
        <CalendarDays className="w-4 h-4 text-slate-400" aria-hidden="true" />
        Customer availability: <strong>{job.customer_availability.join(" or ")}</strong>
      </div>

      <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        Demonstration job — no real booking will be created. Retailer name and exact
        collection address stay hidden until a genuine job is assigned.
      </p>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        {onView && (
          <button
            onClick={() => onView(job)}
            className="flex-1 h-11 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm text-slate-800 transition-colors"
            data-testid={`view-job-${job.id}`}
          >
            View full job
          </button>
        )}
        {onAccept && (
          <button
            onClick={() => onAccept(job)}
            className="flex-1 h-11 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-sm transition-colors"
            data-testid={`accept-job-${job.id}`}
          >
            {acceptLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <div className="text-[11px] text-slate-500 flex items-center gap-1">
        <Icon className="w-3 h-3" aria-hidden="true" /> {label}
      </div>
      <div className="font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
